"use client";

import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { cn } from "@src/lib/utils";
function Sheet({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} data-sentry-element="SheetPrimitive.Root" data-sentry-component="Sheet" data-sentry-source-file="sheet.tsx" />;
}
function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} data-sentry-element="SheetPrimitive.Trigger" data-sentry-component="SheetTrigger" data-sentry-source-file="sheet.tsx" />;
}
function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} data-sentry-element="SheetPrimitive.Close" data-sentry-component="SheetClose" data-sentry-source-file="sheet.tsx" />;
}
function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} data-sentry-element="SheetPrimitive.Portal" data-sentry-component="SheetPortal" data-sentry-source-file="sheet.tsx" />;
}
const SheetOverlay = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>>(({
  className,
  ...props
}, ref) => {
  return <SheetPrimitive.Overlay data-slot="sheet-overlay" ref={ref} className={cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50", className)} {...props} />;
});
SheetOverlay.displayName = "SheetOverlay";
function SheetContent({
  className,
  children,
  side = "right",
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
}) {
  return <SheetPortal data-sentry-element="SheetPortal" data-sentry-component="SheetContent" data-sentry-source-file="sheet.tsx">
      <SheetOverlay data-sentry-element="SheetOverlay" data-sentry-source-file="sheet.tsx" />
      <SheetPrimitive.Content data-slot="sheet-content" className={cn("bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500", side === "right" && "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm", side === "left" && "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm", side === "top" && "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b", side === "bottom" && "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t", className)} {...props} data-sentry-element="SheetPrimitive.Content" data-sentry-source-file="sheet.tsx">
        {children}
        <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none" data-sentry-element="SheetPrimitive.Close" data-sentry-source-file="sheet.tsx">
          <XIcon className="size-4" data-sentry-element="XIcon" data-sentry-source-file="sheet.tsx" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>;
}
function SheetHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div data-slot="sheet-header" className={cn("flex flex-col gap-1.5 p-4", className)} {...props} data-sentry-component="SheetHeader" data-sentry-source-file="sheet.tsx" />;
}
function SheetFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return <div data-slot="sheet-footer" className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} data-sentry-component="SheetFooter" data-sentry-source-file="sheet.tsx" />;
}
function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return <SheetPrimitive.Title data-slot="sheet-title" className={cn("text-foreground font-semibold", className)} {...props} data-sentry-element="SheetPrimitive.Title" data-sentry-component="SheetTitle" data-sentry-source-file="sheet.tsx" />;
}
function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return <SheetPrimitive.Description data-slot="sheet-description" className={cn("text-muted-foreground text-sm", className)} {...props} data-sentry-element="SheetPrimitive.Description" data-sentry-component="SheetDescription" data-sentry-source-file="sheet.tsx" />;
}
export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };